<template>
  <div class="vuse-content-wrapper">
    <vuse-section-definition
        :title="sectionNameText"
        :companyName="companyNameText"
        icon="mdi-lightbulb-group"/>
    <v-container fluid class="fill-width">
      <v-sheet class="neu-glow with-radius">
        <v-data-table
            :loading="getLoader"
            :headers="filteredHeaders"
            :items="groups"
            :single-expand="true"
            item-key="groupId"
            sort-by="groupId"
            class="elevation-1 crud-table px-6 neu-glow"
        >
          <template v-slot:top>
            <v-toolbar flat color="transparent" class="pt-2">
              <v-spacer/>
              <v-btn color="primary" v-if="isRolSuper || isRolSiteAdmin" dark class="mb-2" @click="newGroup">{{ $t('Global.Groups.newGroup') }}</v-btn>
              <groups-dialog
                  v-if="dialogActive"
                  :activate="dialogActive"
                  :item="editedItem"
                  :isViewSuper="isViewSuper"
                  @create-item="createItem"
                  @update-item="updateItem"
                  @close-dialog="dialogActive=false"/>
            </v-toolbar>
          </template>

          <template #[`item.nodes`]="{ item }">
            <span>{{getNodesDesc(item)}}</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-tooltip content-class='custom-tooltip' left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    delay-open="200"
                    :open-on-click="false"
                    :open-on-focus="false"
                    @click="editItem(item)"
                >mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('Global.Groups.edit') }}</span>
            </v-tooltip>
            <v-tooltip content-class='custom-tooltip' right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    delay-open="200"
                    :open-on-click="false"
                    :open-on-focus="false"
                    @click="deleteItem(item)"
                >mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('Global.Groups.delete') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-sheet>
    </v-container>
    <confirm-dialog
        ref="confirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMessage"
    />
    <v-snackbar
        v-model="snackbar"
        absolute
        top
        right
        :timeout="snackbarTimeout"
        :color="snackbarColor"
        class="mt-6"
    >
      <span>{{ snackbarMessage }}</span>
      <v-icon class="ml-2" dark>{{ snackbarIcon }}</v-icon>
      <template v-slot:action="{ attrs }">
        <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          {{ $t("Global.ConfirmDialog.buttonTextClose") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import GroupApi from "@/mixins/Services/GroupApi";
import snackBar from "@/mixins/snackBarMixin";
import loader from "@/mixins/Loader";
import Utils from "@/mixins/Utils";
import Logout from "@/mixins/Logout";
import ConfirmDialog from "@/components/UI/Dialogs/ConfirmDialog";
import GroupsDialog from './GroupsDialog.vue'
import {companiesMock} from "@/infrastructure/MocksResponses";
import {mapGetters} from 'vuex'
import ROL from "@/config/rol-values";

export default {
  name: "Groups",
  components: {
    GroupsDialog,
    ConfirmDialog
  },
  mixins: [GroupApi, snackBar, loader, Utils, Logout],
  data() {
    return {
      groups: [],
      nodeByGroups: [],
      headers: [
        {text: this.$t('Global.Groups.groupName'), value: "groupName", align: "left", sortable: true},
        {text: this.$t('Global.Groups.nodes'), value: "nodes", align: "left", sortable: false},
        {text: this.$t('Global.Groups.actions'), value: "actions", align: "center", sortable: false, width: "130px"},
        {text: '', value: 'data-table-expand'}
      ],
      editedItem: null,
      dialogActive: false,
      confirmDialogTitle: "",
      confirmDialogMessage: null,
      companiesList: companiesMock,
      rol: ROL,
      isViewSuper: false,
      nodesByGroup: []
    }
  },
  computed: {
    ...mapGetters(['getCompanyName','getEditCompanyInfoName', "getRoleId", "getDefsRolesTypes"]),
    filteredHeaders() {
      if (this.headers.length) {
         return this.isRolSuper || this.isRolSiteAdmin
             ? this.headers
             : this.headers.filter(h => h.value !== "actions")

      }
      else return []
    },
    isRolSuper(){
      return [this.rol.SUPER_ADMIN, this.rol.SUPER_USER].includes(this.getRoleId)
    },
    isRolSiteAdmin(){
      return [this.rol.SITE_ADMIN].includes(this.getRoleId)
    },
    sectionNameText(){
      return this.$t('Global.Main.groupsManagement')
    },
    companyNameText(){
      return this.isViewSuper
          ? ''
          : this.getEditCompanyInfoName || this.getCompanyName
    }
  },
  watch: {
    '$route.meta.viewSuperUser'() {
      this.isViewSuper = this.$route.meta.viewSuperUser
      this.getUsersList()
    },
    deep: true,
    immediate: true
  },
  methods: {
    lockHandler(item, value) {
      const toUpdateItem = Object.assign({}, item)
      toUpdateItem.locked = Number(value)
      this.updateItem(toUpdateItem, this.$t("Global.Groups.blockUser"))
    },
    getRoleType(item) {
      return this.getDefsRolesTypes?.find((e) => e.roleId === item.roleId)?.roleName
    },
    getCompany(item) {
      return this.companiesList.find((e) => e.companyId === item.companyId)?.companyName
    },
    async getGroupsList() {
      try {
        this.setLoader(true)
        this.groups = await this.getGroups();
        this.setLoader(false)
      } catch (e) {
        this.groups = []
        console.error('[GROUPS][getGroups] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    getNodesDesc(item) {

      var nodesFiltered = this.nodesByGroup?.filter((n) => n.groupId === item.groupId)

      var nodesText = ""

      const maxItems = 8
      const etcEtc = "(...)"
      var cont = 1

      nodesFiltered.forEach(n => {
          if (nodesText === ""){
            nodesText = n.nodeExtension + " - " + n.nodeName
          }
          else{
            if (cont === maxItems){
              nodesText = nodesText.concat(", " + etcEtc)
              cont++
            }
            else if(cont < maxItems) {
              nodesText = nodesText.concat(", " + n.nodeExtension + " - " + n.nodeName)
              cont++
            }
          }
        }
      )

      return nodesText
    },
    async getGroupNodes() {
      try {
        this.setLoader(true)
        this.nodesByGroup = await this.getNodeNamesByGroupAndCompany();
        this.setLoader(false)
      } catch (e) {
        this.groups = []
        console.error('[GROUPS][getNodeNamesByGroupAndCompany] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }     
    },
    newGroup() {
      this.dialogActive = true
      this.editedItem = null
    },
    editItem(item) {
      this.dialogActive = true
      this.editedItem = item
    },
    async createItem(item) {
      this.confirmDialogTitle = this.$t("Global.Groups.newGroup")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.createGroup(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Groups.groupCreatedOk"), "success", "4000", "mdi-check-circle")
        this.getGroupsList()
        this.getGroupNodes()
      } catch (e) {
        console.error('[GROUPS][createGroup] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async updateItem(item) {
      this.confirmDialogTitle = this.$t("Global.Groups.editGroup")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.updateGroup(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Groups.groupEditedOk"), "success", "4000", "mdi-check-circle")
        this.getGroupsList()
        this.getGroupNodes()
      } catch (e) {
        console.error('[GROUPS][updateGroup] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async deleteItem(item) {
      this.confirmDialogTitle = this.$t("Global.Groups.deleteGroup")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        await this.deleteGroup(item.groupId)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Groups.groupDeletedOk"), "success", "4000", "mdi-check-circle")
        this.getGroupsList()
        this.getGroupNodes()
      } catch (e) {
        console.error('[GROUPS][deleteGroup] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    }
  },
  mounted() {    
    this.isViewSuper = this.$route.meta.viewSuperUser
    this.getGroupsList()
    this.getGroupNodes()
  }
}
</script>